import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { FaHome, FaInfoCircle, FaEnvelope, FaBuilding, FaHardHat, FaWindowRestore, FaDraftingCompass, FaFileContract, FaShieldAlt, FaNewspaper, FaWpforms } from 'react-icons/fa'
import { supabase } from '../lib/supabase'

const SitemapPage = () => {
    const { t, i18n } = useTranslation()
    const [posts, setPosts] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                const { data, error } = await supabase
                    .from('posts')
                    .select('id, title_en, title_es, slug')
                    .eq('is_published', true)
                    .order('created_at', { ascending: false })

                if (error) throw error
                setPosts(data || [])
            } catch (error) {
                console.error('Error fetching sitemap posts:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchPosts()
    }, [])

    const baseSections = [
        {
            title: t('sitemap.general'),
            links: [
                { label: t('nav.home'), to: '/', icon: FaHome },
                { label: t('nav.about'), to: '/about', icon: FaInfoCircle },
                { label: t('nav.contact'), to: '/contact', icon: FaEnvelope },
                { label: t('nav.blog'), to: '/blog', icon: FaFileContract },
            ]
        },
        {
            title: t('sitemap.forms'),
            links: [
                { label: t('admin.monday.new_application'), to: '/forms/architecture/new-quote', icon: FaWpforms },
                { label: t('admin.monday.new_scope'), to: '/forms/architecture/new-scope', icon: FaWpforms },
                { label: t('admin.monday.construction_new_project'), to: '/forms/construction/new-quote', icon: FaWpforms },
                { label: t('admin.monday.roofing_new_project'), to: '/forms/roofing/new-quote', icon: FaWpforms },
                { label: t('admin.monday.impact_windows_new_project'), to: '/forms/impact-windows/new-quote', icon: FaWpforms },
                { label: t('admin.monday.new_application') + ' — ' + t('thank_you.title'), to: '/forms/architecture/new-quote/thank-you', icon: FaWpforms },
                { label: t('admin.monday.new_scope') + ' — ' + t('thank_you.title'), to: '/forms/architecture/new-scope/thank-you', icon: FaWpforms },
                { label: t('admin.monday.construction_new_project') + ' — ' + t('thank_you.title'), to: '/forms/construction/new-quote/thank-you', icon: FaWpforms },
                { label: t('admin.monday.roofing_new_project') + ' — ' + t('thank_you.title'), to: '/forms/roofing/new-quote/thank-you', icon: FaWpforms },
                { label: t('admin.monday.impact_windows_new_project') + ' — ' + t('thank_you.title'), to: '/forms/impact-windows/new-quote/thank-you', icon: FaWpforms }
            ]
        },
        {
            title: t('sitemap.business_units'),
            links: [
                { label: t('nav.architecture'), to: '/architecture', icon: FaDraftingCompass },
                { label: t('nav.construction'), to: '/construction', icon: FaHardHat },
                { label: t('nav.windows'), to: '/impact-windows', icon: FaWindowRestore },
                { label: t('nav.roofing'), to: '/roofing', icon: FaHome },
            ]
        },
        {
            title: t('sitemap.legal'),
            links: [
                { label: t('footer.privacy_policy'), to: '/privacy-policy', icon: FaShieldAlt },
                { label: t('footer.terms_of_service'), to: '/terms-of-service', icon: FaFileContract },
            ]
        }
    ]

    const blogSection = loading ? {
        title: t('nav.blog'),
        isPlaceholder: true,
        links: [1, 2, 3].map(i => ({ label: 'Loading...', to: '#', isPlaceholder: true }))
    } : (posts.length > 0 ? {
        title: t('nav.blog'),
        links: posts.map(post => ({
            label: i18n.language.startsWith('es') ? (post.title_es || post.title_en) : (post.title_en || post.title_es) || 'Untitled',
            to: `/blog/${post.slug}`,
            icon: FaNewspaper
        }))
    } : null)

    const allSections = blogSection ? [baseSections[0], baseSections[1], blogSection, baseSections[2]] : baseSections
    const sections = allSections.filter(section => section && section.links && section.links.length > 0)

    const container = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    }

    const item = {
        hidden: { opacity: 0, y: 20 },
        show: { opacity: 1, y: 0 }
    }

    return (
        <div className="px-4 pt-24 pb-12 min-h-screen bg-background sm:px-6 lg:px-8">
            <div className="mx-auto max-w-6xl">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-12 text-center"
                >
                    <h1 className="mb-4 text-4xl font-bold text-primary">{t('sitemap.title')}</h1>
                    <p className="text-muted-foreground">{t('sitemap.description')}</p>
                </motion.div>

                <motion.div
                    variants={container}
                    initial="hidden"
                    animate="show"
                    className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                >
                    {sections.map((section, index) => (
                        <motion.div
                            key={index}
                            variants={item}
                            className="p-6 rounded-xl border shadow-sm transition-shadow bg-card border-border hover:shadow-md h-fit"
                        >
                            <h2 className="pb-2 mb-4 text-xl font-bold border-b text-foreground border-border">
                                {section.title}
                            </h2>
                            <ul className="space-y-3">
                                {section.links.map((link, linkIndex) => {
                                    if (section.isPlaceholder || link.isPlaceholder) {
                                        return (
                                            <li key={linkIndex} className="flex items-center animate-pulse">
                                                <div className="mr-3 w-8 h-8 rounded-lg bg-muted"></div>
                                                <div className="w-32 h-4 rounded bg-muted"></div>
                                            </li>
                                        )
                                    }
                                    const Icon = link.icon || FaNewspaper
                                    return (
                                        <li key={linkIndex}>
                                            <Link
                                                to={link.to}
                                                className="flex items-center transition-colors text-muted-foreground hover:text-primary group"
                                            >
                                                <span className="p-2 mr-3 rounded-lg transition-colors bg-muted group-hover:bg-primary/10 shrink-0">
                                                    <Icon className="w-4 h-4 text-primary" />
                                                </span>
                                                <span className="text-sm font-medium line-clamp-1">{link.label}</span>
                                            </Link>
                                        </li>
                                    )
                                })}
                            </ul>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </div>
    )
}

export default SitemapPage
